// Layout.jsx — CONTAINER da casca (dono da lógica). Monta { vm, actions } e renderiza a
// LayoutView (View pura do Claude Design, formato View/ViewModel). Lógica portada VERBATIM
// do Layout legado (backup: Layout.legacy.jsx.bak): flags de menu, labels/ícones por tema,
// prefs de e-mail, tickets, edição de nome, exclusão de conta, deep-links de notificação,
// toasts e tracking. O colapsável de configurações da sidebar virou o Dialog "Configurações"
// (engrenagem no header da LayoutView). NotificationCenter real entra via slot headerExtras.
import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/authService';
import whatsappService from '../services/whatsappService';
import { useMeter } from '../hooks/useEntitlements';
import usersService from '../services/usersService';
import { isDevEnvironment, isPlansSaleEnabled, isIntegrationsEnabled } from '../utils/environment';
import DeleteAccountDialog from './DeleteAccountDialog';
import NotificationCenter from './NotificationCenter';
import NotificationDetailModal from './NotificationDetailModal';
import NotificationErrorBoundary from './NotificationErrorBoundary';
import NotificationToast from './NotificationToast';
import AnnouncementBanner from './AnnouncementBanner';
import { useNotificationToast } from '../hooks/useNotificationToast';
import { useThemeLabels } from '../hooks/useThemeLabels';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useNotificationCenter } from '../contexts/NotificationCenterContext';
import { trackPageView, trackEvent } from '../utils/tracking';
import { navManifest } from '../../design/nav.manifest.mjs';
import LayoutView from '../shell/LayoutView';
import SettingsView from '../shell/SettingsView';

export default function Layout() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const { openNotificationCenter } = useNotificationCenter();
  const [deepLinkNotificationId, setDeepLinkNotificationId] = useState(null);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [loadingEmailPref, setLoadingEmailPref] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameLoading, setEditNameLoading] = useState(false);
  const [editNameError, setEditNameError] = useState('');
  const [displayName, setDisplayName] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = authService.getUserInfo();
  const { getLabel, getIcon } = useThemeLabels();
  const { availableThemes, themeId, changeTheme, loading: themeLoading } = useThemeCustom();
  const { activeToasts, enqueue: enqueueToast, dismiss: dismissToast, pauseTimer, resumeTimer } = useNotificationToast();

  // Load email notification preference
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await usersService.getNotificationPreferences();
        setEmailNotificationsEnabled(prefs.emailNotificationsEnabled);
      } catch (error) {
        console.warn('[Layout] Could not load notification preferences:', error);
      }
    };
    loadPreferences();
  }, []);

  // Handle email notifications toggle (contrato: recebe o boolean, não o event)
  const handleEmailNotificationsToggle = useCallback(async (newValue) => {
    setLoadingEmailPref(true);
    try {
      await usersService.updateNotificationPreferences(newValue);
      setEmailNotificationsEnabled(newValue);
      trackEvent('email_notifications_toggle', { enabled: newValue });
    } catch (error) {
      console.error('[Layout] Failed to update notification preferences:', error);
    } finally {
      setLoadingEmailPref(false);
    }
  }, []);

  const handleEditNameOpen = () => {
    setEditNameValue(displayName || userInfo?.name || '');
    setEditNameError('');
    setEditNameOpen(true);
  };

  const handleEditNameSave = useCallback(async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      setEditNameError('O nome não pode ser vazio');
      return;
    }
    if (trimmed.length > 255) {
      setEditNameError('Nome muito longo (máximo 255 caracteres)');
      return;
    }
    setEditNameLoading(true);
    setEditNameError('');
    try {
      await usersService.updateName(trimmed);
      authService.setLocalName(trimmed);
      setDisplayName(trimmed);
      setEditNameOpen(false);
      trackEvent('user_name_updated');
    } catch (error) {
      console.error('[Layout] Failed to update name:', error);
      setEditNameError('Erro ao salvar o nome. Tente novamente.');
    } finally {
      setEditNameLoading(false);
    }
  }, [editNameValue]);

  // Check for notification deep-link querystring (from email click)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openNotifications') === 'true') {
      openNotificationCenter();
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location]);

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/^#notification=(.+)$/);
    if (match) {
      setDeepLinkNotificationId(match[1]);
      window.history.replaceState(null, '', location.pathname + location.search);
    }
  }, [location]);

  // Track page views on navigation
  useEffect(() => {
    const pageName = location.pathname.replace(/^\//, '') || 'dashboard';
    trackPageView(pageName);
  }, [location.pathname]);

  const handleLogout = () => {
    trackEvent('admin_logout', { user_id: userInfo?.sub || 'unknown' });
    authService.removeToken();
    authService.redirectToLogin();
  };

  const handleMenuClick = (path) => {
    trackEvent('menu_navigation', { destination: path, from: location.pathname });
    navigate(path);
  };

  // "Central de ajuda" (rodapé do drawer) aciona o mesmo botão do widget flutuante de suporte.
  const handleOpenSupportWidget = useCallback(() => {
    const widgetButton = document.querySelector('.agendario-support-widget-button');
    if (widgetButton && typeof widgetButton.click === 'function') {
      widgetButton.click();
      return;
    }
    console.warn('[Layout] Support widget button not found (.agendario-support-widget-button).');
  }, []);

  // ── nav — FONTE ÚNICA: design/nav.manifest.mjs (mesma lista do build:design).
  // Aqui só se aplica tema (getLabel/getIcon) e resolvem os `conditional` (kill-switches/flags).
  const conditions = {
    plansSale: isPlansSaleEnabled(),        // venda de planos oculta por padrão (VITE_FEATURE_PLANS_SALE)
    integrations: isIntegrationsEnabled(),  // WhatsApp liberado p/ todos; kill-switch = VITE_FEATURE_INTEGRATIONS='false'
    devOnly: isDevEnvironment(),
  };
  const navItems = navManifest
    .filter((i) => !i.conditional || conditions[i.conditional])
    .map((i) => {
      // getLabel ecoa a chave quando o tema não tem o label → o fallback é o 2º arg (manifest).
      // getIcon devolve o sentinela HelpOutline quando não acha → cai pro ícone do manifest.
      const themeIcon = getIcon(i.key);
      return {
        key: i.key,
        label: getLabel(i.key, i.label),
        iconName: themeIcon?.name && themeIcon.name !== 'HelpOutline' ? themeIcon.name : i.iconName,
        route: i.route,
        migrated: true, // no Admin todas as rotas existem — sem "em breve"
      };
    });

  // ── Chip WhatsApp do header (layout.vm @v5) ─────────────────────────────────
  // visible = mesmo kill-switch do menu Integrações; 'disabled' = integração desligada
  // nas settings do dono; saldo/low vêm do snapshot de entitlements (useMeter, que já
  // tem polling de 60s — ver useEntitlements.js).
  const integrationsOn = conditions.integrations;
  const { balance: waBalance, low: waLow, loading: waMeterLoading } = useMeter('whatsapp_messages');
  const { data: waSettings, isLoading: waSettingsLoading } = useQuery({
    queryKey: ['whatsapp-settings'],
    queryFn: () => whatsappService.getSettings(),
    enabled: integrationsOn,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const waStatus = (waMeterLoading || waSettingsLoading) ? 'loading'
    : !waSettings?.enabled ? 'disabled'
    : waBalance <= 0 ? 'depleted'
    : waLow ? 'low'
    : 'ok';
  const waCreditsLabel = (waStatus === 'loading' || waStatus === 'disabled')
    ? null
    : waBalance.toLocaleString('pt-BR');
  const waTooltip = {
    loading: 'Carregando saldo…',
    disabled: 'WhatsApp não configurado — clique para ativar',
    depleted: 'Saldo esgotado — envios de WhatsApp pausados',
    low: `Pouco saldo — ${waCreditsLabel} créditos restantes`,
    ok: `WhatsApp ativo — ${waCreditsLabel} créditos`,
  }[waStatus];

  // ── ViewModel + actions da casca ─────────────────────────────────────────────
  const userName = displayName || userInfo?.name || 'Usuário';
  const vm = {
    nav: navItems,
    header: {
      appTitle: getLabel('appTitle'),
      appSubtitle: getLabel('appSubtitle'),
      userName,
      userInitials: userName.charAt(0).toUpperCase(),
      notificationsCount: 0, // sino embutido não é usado — NotificationCenter real via headerExtras
      whatsapp: { visible: integrationsOn, status: waStatus, creditsLabel: waCreditsLabel, tooltip: waTooltip },
    },
    activeRoute: location.pathname,
  };

  const actions = {
    onNavigate: handleMenuClick,
    onLogout: handleLogout,
    onOpenNotifications: openNotificationCenter,
    onOpenSettings: () => setSettingsOpen(true),
    onOpenSupportWidget: handleOpenSupportWidget,
    onOpenWhatsApp: () => {
      trackEvent('header_whatsapp_chip_click', { status: waStatus });
      navigate('/integrations');
    },
  };

  // NotificationCenter real (sino + badge + painel + SignalR + toasts) no header da View.
  const headerExtras = (
    <NotificationErrorBoundary>
      <NotificationCenter onToast={enqueueToast} />
    </NotificationErrorBoundary>
  );

  // ── ViewModel + actions do dialog de Configurações (contrato: shell/settings.vm.js @v2) ──
  const settingsVm = {
    open: settingsOpen,
    userName,
    email: userInfo?.email || '',
    emailNotificationsEnabled,
    savingEmailPref: loadingEmailPref,
    editName: { open: editNameOpen, value: editNameValue, error: editNameError || null, saving: editNameLoading },
    // "Tipo de negócio" = tema do tenant (saiu do header no layout.vm @v3)
    businessType: { currentId: themeId, options: availableThemes, saving: themeLoading },
  };
  const settingsActions = {
    onClose: () => setSettingsOpen(false),
    onToggleEmailNotifications: handleEmailNotificationsToggle,
    onOpenEditName: handleEditNameOpen,
    onChangeEditNameValue: setEditNameValue,
    onSaveEditName: handleEditNameSave,
    onCancelEditName: () => { if (!editNameLoading) setEditNameOpen(false); },
    onOpenTerms: () => window.open('/termos-de-uso', '_blank'),
    onDeleteAccount: () => { setSettingsOpen(false); setDeleteAccountOpen(true); },
    onChangeBusinessType: changeTheme,
  };

  return (
    <>
      <LayoutView vm={vm} actions={actions} headerExtras={headerExtras}>
        <AnnouncementBanner />
        <Outlet />
      </LayoutView>

      {/* Dialog de Configurações — View do designer (inclui o sub-dialog "Editar nome") */}
      <SettingsView vm={settingsVm} actions={settingsActions} />

      {/* Deep link notification modal */}
      <NotificationDetailModal
        open={!!deepLinkNotificationId}
        onClose={() => setDeepLinkNotificationId(null)}
        notificationId={deepLinkNotificationId}
      />

      {/* Toast notifications */}
      <NotificationToast
        toasts={activeToasts}
        onDismiss={dismissToast}
        onPauseTimer={pauseTimer}
        onResumeTimer={resumeTimer}
      />

      {/* Dialog de exclusão de conta */}
      <DeleteAccountDialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        userEmail={userInfo?.email}
      />
    </>
  );
}
